import { useEffect, useState } from 'preact/hooks';
import { COSTS, RESOURCES, SPECIAL_BUILD_MIN_PLAYERS, houseRules, res, suggestDiscard, totalResources, turnKey, type Action, type DevCardType, type ProgressCard, type Resource, type Resources } from '../engine';
import { useHandDeltas } from './effects';
import type { GameClient } from './game-client';
import { DEV_CARD_LABELS, RESOURCE_LABELS } from './theme';
import { PlayerName, ResourceChip, ResourceList, ResourcePicker } from './widgets';

export type BuildMode = 'road' | 'settlement' | 'city' | null;

export function has(legal: readonly Action[], type: Action['type']): boolean {
  return legal.some((a) => a.type === type);
}

export function TurnPanel({
  client,
  seat,
  mode,
  setMode,
  run,
}: {
  client: GameClient;
  seat: number;
  mode: BuildMode;
  setMode: (m: BuildMode) => void;
  run: (a: Action, as?: number) => void;
}) {
  const { view, legal, names } = client;
  const me = view.players[seat]!;
  const hand = me.resources ?? res();
  const [picking, setPicking] = useState<'yearOfPlenty' | 'monopoly' | null>(null);
  const [plenty, setPlenty] = useState<Resource[]>([]);
  const gains = useHandDeltas(view.players[seat]!.resources, seat);
  // A half-picked Year of Plenty or Monopoly shouldn't survive into someone else's turn.
  useEffect(() => setPicking(null), [turnKey(view)]);

  if (view.phase.kind === 'gameOver') return null;
  const myTurn = seat === view.currentPlayer;
  const discardOwed = view.phase.kind === 'discard' ? (view.phase.owed[seat] ?? 0) : 0;

  /** Why a build button is off: what's missing from the hand, or "no spot" when it's affordable but nowhere fits. */
  const whyNot = (cost: Resources, legalNow: boolean): string | undefined => {
    if (legalNow) return undefined;
    const short = RESOURCES.filter((r) => hand[r] < cost[r]).map((r) => `${cost[r] - hand[r]} ${RESOURCE_LABELS[r].toLowerCase()}`);
    return short.length ? `Need ${short.join(', ')}` : 'Nowhere to build it right now';
  };
  const buildButton = (m: Exclude<BuildMode, null>, type: Action['type'], label: string) => (
    <button
      class={mode === m ? 'active' : ''}
      data-testid={`build-${m}`}
      disabled={!has(legal, type)}
      title={whyNot(COSTS[m], has(legal, type))}
      onClick={() => setMode(mode === m ? null : m)}
    >
      {label}
    </button>
  );

  const playable = (card: ProgressCard) => {
    const type = { knight: 'playKnight', roadBuilding: 'playRoadBuilding', yearOfPlenty: 'playYearOfPlenty', monopoly: 'playMonopoly' }[card] as Action['type'];
    return has(legal, type);
  };

  const cardCounts = new Map<DevCardType, number>();
  for (const c of me.devCards ?? []) cardCounts.set(c.type, (cardCounts.get(c.type) ?? 0) + 1);
  const playedCounts = new Map<ProgressCard, number>();
  for (const c of me.playedDevCards) playedCounts.set(c, (playedCounts.get(c) ?? 0) + 1);

  return (
    <div class="panel turn">
      <h2>
        <PlayerName seat={seat} names={names} />
        <span class="muted"> · {me.victoryPoints} VP</span>
      </h2>

      {discardOwed > 0 ? (
        <DiscardHand hand={hand} owed={discardOwed} run={run} />
      ) : (
        <div class="hand" data-testid="hand">
          {RESOURCES.map((r) => (
            <ResourceChip key={r} resource={r} count={hand[r]} gained={gains.deltas[r]} gainKey={gains.key} />
          ))}
        </div>
      )}

      <BuildBetweenTurns client={client} seat={seat} run={run} />

      {myTurn && (view.phase.kind === 'main' || view.phase.kind === 'specialBuild') && (
        <>
          <div class="row wrap builds">
            {buildButton('road', 'buildRoad', 'Road')}
            {buildButton('settlement', 'buildSettlement', 'Settlement')}
            {buildButton('city', 'buildCity', 'City')}
            <button
              data-testid="buy-dev"
              disabled={!has(legal, 'buyDevCard')}
              title={has(legal, 'buyDevCard') ? undefined : view.devDeckCount === 0 ? 'No cards left' : whyNot(COSTS.devCard, false)}
              onClick={() => run({ type: 'buyDevCard' })}
            >
              Dev card
            </button>
          </div>
          {mode && <p class="muted">Tap a highlighted spot on the board.</p>}
          <details class="costs">
            <summary>Building costs</summary>
            <div>Road: <ResourceList resources={COSTS.road} /></div>
            <div>Settlement: <ResourceList resources={COSTS.settlement} /></div>
            <div>City: <ResourceList resources={COSTS.city} /></div>
            <div>Dev card: <ResourceList resources={COSTS.devCard} /></div>
          </details>
        </>
      )}

      {((me.devCards?.length ?? 0) > 0 || playedCounts.size > 0) && (
        <div class="devcards" data-testid="devcards">
          <h3>Development cards</h3>
          {[...cardCounts.entries()].map(([type, count]) => (
            <div class="row between" key={type}>
              <span>
                {DEV_CARD_LABELS[type]} ×{count}
              </span>
              {type !== 'victoryPoint' && myTurn && (
                <button
                  class="small"
                  data-testid={`play-${type}`}
                  disabled={!playable(type)}
                  onClick={() => {
                    if (type === 'knight') run({ type: 'playKnight' });
                    else if (type === 'roadBuilding') run({ type: 'playRoadBuilding' });
                    else {
                      setPlenty([]);
                      setPicking(type as 'yearOfPlenty' | 'monopoly');
                    }
                  }}
                >
                  Play
                </button>
              )}
            </div>
          ))}
          {playedCounts.size > 0 && (
            <p class="played muted" data-testid="played-cards">
              Played:{' '}
              {[...playedCounts.entries()].map(([type, count]) => `${DEV_CARD_LABELS[type]} ×${count}`).join(', ')}
            </p>
          )}
        </div>
      )}

      {picking === 'monopoly' && (
        <div class="choice">
          <p>Take every card of one resource from everyone:</p>
          <div class="row wrap">
            {RESOURCES.map((r) => (
              <button
                key={r}
                class="small"
                onClick={() => {
                  setPicking(null);
                  run({ type: 'playMonopoly', resource: r });
                }}
              >
                {RESOURCE_LABELS[r]}
              </button>
            ))}
          </div>
        </div>
      )}
      {picking === 'yearOfPlenty' && (
        <div class="choice">
          <p>Take two from the bank: {plenty.map((r) => RESOURCE_LABELS[r]).join(' + ') || '…'}</p>
          <div class="row wrap">
            {RESOURCES.map((r) => (
              <button
                key={r}
                class="small"
                disabled={view.bank[r] <= plenty.filter((p) => p === r).length}
                onClick={() => {
                  const next = [...plenty, r];
                  if (next.length < 2) return setPlenty(next);
                  setPicking(null);
                  run({ type: 'playYearOfPlenty', resources: [next[0]!, next[1]!] });
                }}
              >
                {RESOURCE_LABELS[r]}
              </button>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

/** The hand, expanded into a picker while a discard is owed: each row shows what you hold and what you're giving up. */
function DiscardHand({ hand, owed, run }: { hand: Resources; owed: number; run: (a: Action) => void }) {
  const [pick, setPick] = useState(res());
  useEffect(() => setPick(res()), [owed]);
  const total = totalResources(pick);
  return (
    <div class="discard-hand" data-testid="discard">
      <p class="discard-title">
        Rolled a 7: discard <strong>{owed}</strong> of your {totalResources(hand)} cards
      </p>
      <ResourcePicker value={pick} onChange={setPick} max={hand} maxTotal={owed} held={hand} testid="discard-pick" />
      <button type="button" class="secondary" data-testid="discard-auto" onClick={() => setPick(suggestDiscard(hand, owed))}>
        Fill in half
      </button>
      <button class="primary" data-testid="discard-submit" disabled={total !== owed} onClick={() => run({ type: 'discard', resources: pick })}>
        Discard {total}/{owed}
      </button>
    </div>
  );
}
/**
 * Building between turns: when it isn't your turn, ask to build once it ends.
 * On a shared screen the current player can also tick the box for the other
 * people at the table.
 */
function BuildBetweenTurns({ client, seat, run }: { client: GameClient; seat: number; run: (a: Action, as?: number) => void }) {
  const { view, legal, names } = client;
  const kind = view.phase.kind;
  const open = !!houseRules(view).specialBuild && view.config.playerCount >= SPECIAL_BUILD_MIN_PLAYERS && kind !== 'setup' && kind !== 'specialBuild' && kind !== 'gameOver';
  if (!open) return null;
  const asked = (i: number) => !!view.buildRequests?.[i];
  if (!client.hotSeat) {
    if (!legal.some((a) => a.type === 'requestBuild')) return null;
    return (
      <button
        class={`small ${asked(seat) ? 'active' : 'secondary'}`}
        data-testid="request-build"
        aria-pressed={asked(seat)}
        title="Build (no trading) when the current turn ends, in play order"
        onClick={() => run({ type: 'requestBuild', on: !asked(seat) })}
      >
        {asked(seat) ? 'Building after this turn ✓' : 'Build after this turn'}
      </button>
    );
  }
  const others = names.map((_, i) => i).filter((i) => i !== view.currentPlayer && !client.cpu?.[i]);
  if (others.length === 0) return null;
  return (
    <div class="build-requests" data-testid="build-requests">
      <span class="muted small">Build after this turn:</span>
      {others.map((i) => (
        <label class="check small" key={i}>
          <input type="checkbox" data-testid={`request-build-${i}`} checked={asked(i)} onChange={() => run({ type: 'requestBuild', on: !asked(i) }, i)} />
          <PlayerName seat={i} names={names} />
        </label>
      ))}
    </div>
  );
}
